import React from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Progress, Select } from 'antd';
import { floor, toLower } from 'lodash';
import {
  AlertScoreWrapper,
  ProgressText,
  LoadingWrapper,
  SmartAssessHeaderPanel,
  Title,
  SelectBoxContainer,
  StyledControlWithLabel,
  StyledCmControls
} from './styled';
import Wait4Me from '../../../../components/Wait4Me';
import HiGoMeLoColors from '../../../common/components/HiGoMeLoColors';
import { normalizeAlertScore } from '../../../../util/normalization';
import FlagRed from '../../../../assets/flag_Red.svg';
import FlagOrange from '../../../../assets/flag_Orange.svg';
import { TM_FRAUD_SOURCE_IDS } from '../../../../util/GlobalConstant';
import {
  convertISOToDateTimeWithoutSeconds,
  convertISOToDateFormat
} from '../../../../util/timezone';

import HeaderPanel from './HeaderPanel';
import HeaderCounterPanel from './HeaderCounterPanel';

const defaultProps = {
  alertDetails: { data: null, isLoading: false },
  alertHeader: null,
  disableSelect: false,
  onChangeAlertQuality: null
};

const AlertTopPanelDetails = ({
  alertDetails,
  alertHeader,
  isPrevention,
  isFromCM,
  alertMetaSuffix,
  ...props
}) => {
  const { data = null, isLoading } = alertDetails;
  const alertQualityList = [
    { id: 'high', name: 'High Quality', color: '#48C93D' },
    { id: 'good', name: 'Good Quality', color: '#0C8ED7' },
    { id: 'low', name: 'Low Quality', color: '#EB9229' }
  ];

  let sampleTransactionDetailsHeaderData = [
    { fieldLabel: 'ID:', fieldValue: '-' },
    { fieldLabel: 'Timestamp:', fieldValue: '-' },
    { fieldLabel: 'Type:', fieldValue: '-' },
    { fieldLabel: 'Category:', fieldValue: '-' }
  ];

  let sampleTransactionAmountHeaderData = [
    { fieldLabel: 'Source Amount:', fieldValue: 'USD 1920' },
    { fieldLabel: 'Source Amount in USD:', fieldValue: 'USD 1920' },
    { fieldLabel: 'Destination Amount:', fieldValue: 'MYR 9000' }
  ];

  let sampleCounterData = [];
  let totalOfSampleData = 0;

  if (data) {
    const {
      txnDateTime,
      origTxnAmount,
      origCurrencyCode,
      oppCurrencyCode,
      oppTxnAmount,
      txnCurrency,
      txnAmount,
      transactionKey,
      transactionTypeCode,
      transactionTypeDescription,
      triggeredRulesActionSummary
    } = data;

    sampleTransactionDetailsHeaderData = [
      { fieldLabel: 'ID:', fieldValue: transactionKey ? transactionKey : '-' },
      {
        fieldLabel: 'Timestamp:',
        fieldValue: txnDateTime
          ? convertISOToDateTimeWithoutSeconds(txnDateTime)
          : '-'
      },
      { fieldLabel: 'Type:', fieldValue: transactionTypeCode || '-' },
      { fieldLabel: 'Category:', fieldValue: transactionTypeDescription || '-' }
    ];

    sampleTransactionAmountHeaderData = [
      {
        fieldLabel: 'Source Amount:',
        fieldValue: origTxnAmount
          ? `${origTxnAmount} ${origCurrencyCode || ''}`
          : '-'
      },
      {
        fieldLabel: 'Source Amount in USD:',
        fieldValue: txnAmount ? `${txnAmount} ${txnCurrency || ''}` : '-'
      },
      {
        fieldLabel: 'Destination Amount:',
        fieldValue: oppTxnAmount
          ? `${oppTxnAmount} ${oppCurrencyCode || ''}`
          : '-'
      }
    ];

    try {
      const counterData = JSON.parse(triggeredRulesActionSummary);

      Object.keys(counterData).forEach((counterLabel) => {
        totalOfSampleData += counterData[counterLabel];
        sampleCounterData.push({
          labelText: `Action: ${String(counterLabel).toLowerCase()}`,
          valueText: counterData[counterLabel]
        });
      });
    } catch {}
  }

  if (isPrevention) {
    return (
      <>
        {!isFromCM && alertHeader && (
          <SmartAssessHeaderPanel>
            {alertHeader}
            {data?.alertSourceId === TM_FRAUD_SOURCE_IDS.TM && (
              <span>
                &nbsp;&nbsp;|&nbsp;&nbsp;
                <img
                  alt=""
                  style={{ height: '26px', paddingBottom: '2px' }}
                  src={FlagOrange}
                />
                &nbsp; AML-TM Detection
              </span>
            )}
            {data?.alertSourceId === TM_FRAUD_SOURCE_IDS.FRAUD && (
              <span>
                &nbsp;&nbsp;|&nbsp;&nbsp;
                <img
                  alt=""
                  style={{ height: '26px', paddingBottom: '2px' }}
                  src={FlagRed}
                />
                &nbsp; Fraud Detection
              </span>
            )}

            {data?.alertSourceId !== TM_FRAUD_SOURCE_IDS.TM &&
              data?.alertSourceId !== TM_FRAUD_SOURCE_IDS.FRAUD && (
                <span>
                  &nbsp;&nbsp;|&nbsp;&nbsp;
                  <img
                    alt=""
                    style={{ height: '26px', paddingBottom: '2px' }}
                    src={FlagRed}
                  />
                  &nbsp; Fraud Prevention
                </span>
              )}
          </SmartAssessHeaderPanel>
        )}

        {isLoading ? (
          <LoadingWrapper>
            <Wait4Me />
          </LoadingWrapper>
        ) : (
          <>
            {isFromCM && (
              <StyledCmControls>
                <StyledControlWithLabel label="Alert ID">
                  {data?.alertId}
                </StyledControlWithLabel>
                <StyledControlWithLabel label="Alert Source">
                  Fraud Prevention
                </StyledControlWithLabel>
                <StyledControlWithLabel label="Alert Date">
                  {convertISOToDateFormat(data?.alertDate)}
                </StyledControlWithLabel>
                {alertMetaSuffix}
              </StyledCmControls>
            )}
            <Row gutter={[20, 10]} justify="start" wrap={false}>
              <Col>
                <HeaderPanel
                  title="Transaction Details"
                  data={sampleTransactionDetailsHeaderData}
                />
              </Col>
              <Col>
                <HeaderPanel
                  title="Transaction Amount"
                  data={sampleTransactionAmountHeaderData}
                />
              </Col>

              <Col>
                <HeaderCounterPanel
                  data={{
                    labelText: 'Total Variants Breached',
                    valueText: totalOfSampleData
                  }}
                />
              </Col>
              {sampleCounterData.map((counterData, counterIndex) => {
                return (
                  <Col>
                    <HeaderCounterPanel key={counterIndex} data={counterData} />
                  </Col>
                );
              })}
              {!isFromCM && (
                <Col>
                  <Title>Alert Quality</Title>
                  <SelectBoxContainer>
                    <Select
                      style={{ minWidth: '165px' }}
                      disabled={isLoading || props?.disableSelect}
                      defaultValue={
                        data?.alertQuality && toLower(data?.alertQuality)
                      }
                      placeholder="Please Select"
                      onChange={(value) =>
                        props?.onChangeAlertQuality(value, data?.alertSourceId)
                      }>
                      {alertQualityList?.map(({ id, name, color }) => (
                        <Option key={id} value={id} style={{ color }}>
                          {name}
                        </Option>
                      ))}
                    </Select>
                  </SelectBoxContainer>
                </Col>
              )}
            </Row>
          </>
        )}
      </>
    );
  } else {
    return (
      <>
        {alertHeader ? (
          <SmartAssessHeaderPanel>
            {alertHeader}
            {data?.alertSourceId === TM_FRAUD_SOURCE_IDS.TM && (
              <span>
                &nbsp;&nbsp;|&nbsp;&nbsp;
                <img
                  alt=""
                  style={{ height: '26px', paddingBottom: '2px' }}
                  src={FlagOrange}
                />
                &nbsp; AML-TM Detection
              </span>
            )}
            {data?.alertSourceId === TM_FRAUD_SOURCE_IDS.FRAUD && (
              <span>
                &nbsp;&nbsp;|&nbsp;&nbsp;
                <img
                  alt=""
                  style={{ height: '26px', paddingBottom: '2px' }}
                  src={FlagRed}
                />
                &nbsp; Fraud Detection
              </span>
            )}
          </SmartAssessHeaderPanel>
        ) : null}

        {isLoading ? (
          <LoadingWrapper>
            <Wait4Me />
          </LoadingWrapper>
        ) : (
          <Row gutter={[40, 20]} justify="start" wrap={false}>
            <Col>
              <Title>Alert Score</Title>
              <AlertScoreWrapper>
                <Progress
                  percent={data?.alertScore}
                  showInfo={false}
                  strokeColor={HiGoMeLoColors(data?.alertLevel)}
                />
                <ProgressText>
                  <p>
                    <b>
                      {data?.alertLevel}{' '}
                      {normalizeAlertScore(floor(data?.alertScore, 2))}
                    </b>
                    /100
                  </p>
                </ProgressText>
              </AlertScoreWrapper>
            </Col>

            <Col>
              <Title>Alert Quality</Title>
              <SelectBoxContainer>
                <Select
                  style={{ minWidth: '165px' }}
                  disabled={isLoading || props?.disableSelect}
                  defaultValue={
                    data?.alertQuality && toLower(data?.alertQuality)
                  }
                  placeholder="Please Select"
                  onChange={(value) =>
                    props?.onChangeAlertQuality(value, data?.alertSourceId)
                  }>
                  {alertQualityList?.map(({ id, name, color }) => (
                    <Option key={id} value={id} style={{ color }}>
                      {name}
                    </Option>
                  ))}
                </Select>
              </SelectBoxContainer>
            </Col>
          </Row>
        )}
      </>
    );
  }
};

AlertTopPanelDetails.defaultProps = defaultProps;
AlertTopPanelDetails.propTypes = {
  alertHeader: PropTypes.string,
  isLoading: PropTypes.bool,
  data: PropTypes.object,
  error: PropTypes.object,
  disableSelect: PropTypes.bool,
  isFromCM: PropTypes.bool,
  alertMetaSuffix: PropTypes.element
};

export default AlertTopPanelDetails;
